/**
 * hw-cart-upsells.js
 *
 * 1. Drag-to-scroll (mouse) on slider containers.
 * 2. Quick-add-to-cart via ATC buttons.
 * 3. S&D recommendations fetch for data_source=recommendations mode.
 */

// ── Drag-to-scroll (IIFE-scoped to avoid global variable conflicts) ───────────

(() => {
  const SCROLL_MULTIPLIER = 1.5;

  let isDragging = false;
  let activeSlider = null;
  let startX = 0;
  let scrollLeft = 0;

  function getSlider(target) {
    return target?.closest?.('.hw-cart-upsells__slider') ?? null;
  }

  function stopDragging() {
    if (!activeSlider) return;
    activeSlider.classList.remove('hw-cu-dragging');
    isDragging = false;
    activeSlider = null;
  }

  document.addEventListener('mousedown', (e) => {
    const slider = getSlider(e.target);
    if (!slider) return;
    isDragging = true;
    activeSlider = slider;
    startX = e.pageX - slider.offsetLeft;
    scrollLeft = slider.scrollLeft;
    slider.classList.add('hw-cu-dragging');
    e.preventDefault();
  });

  document.addEventListener('mouseup', stopDragging);
  document.addEventListener('mouseleave', stopDragging);

  document.addEventListener('mousemove', (e) => {
    if (!isDragging || !activeSlider) return;
    e.preventDefault();
    const x = e.pageX - activeSlider.offsetLeft;
    activeSlider.scrollLeft = scrollLeft - (x - startX) * SCROLL_MULTIPLIER;
  });
})();

// ── Slider nav arrows ───────────────────────────────────────────────────────

document.addEventListener('click', (e) => {
  const btn = e?.target?.closest?.(
    '.hw-cart-upsells .hw-carousel-navigation__control, .hw-cart-upsells .hw-carousel-navigation .slideshow-control'
  );
  if (!btn) return;
  const ref = btn.getAttribute('ref');
  let direction = btn.dataset.direction;
  if (ref === 'previous') direction = 'prev';
  if (ref === 'next') direction = 'next';
  if (direction !== 'prev' && direction !== 'next') return;
  const block = btn.closest('.hw-cart-upsells');
  const slider = block?.querySelector('.hw-cart-upsells__slider');
  if (!slider) return;
  e.preventDefault();
  const cardW = parseFloat(getComputedStyle(block).getPropertyValue('--hw-cu-card-w')) || 144;
  const gap = parseFloat(getComputedStyle(block).getPropertyValue('--hw-cu-gap')) || 16;
  const step = cardW + gap;
  const delta = direction === 'next' ? step : -step;
  slider.scrollBy({ left: delta, behavior: 'smooth' });
  scheduleUpsellsNavDisabledSync(block);
});

/**
 * Cart upsells keeps nav color scheme on arrows (opacity only when disabled).
 * Clears scheme classes left by global applyHwNavDisabledColorScheme on first paint.
 *
 * @param {Element} navArrows - `.hw-carousel-navigation__arrows`
 */
function syncUpsellsNavColorScheme(navArrows) {
  const navigationRoot = navArrows.closest('.hw-carousel-navigation');
  if (!navigationRoot || navigationRoot.dataset.hwCartUpsellsNav !== 'true') {
    return;
  }

  const enabledScheme = navigationRoot.dataset.enabledColorScheme;
  if (!enabledScheme) {
    return;
  }

  const disabledScheme = navigationRoot.dataset.disabledColorScheme ?? '';
  const buttons = navArrows.querySelectorAll(
    '.slideshow-control, .hw-carousel-navigation__control'
  );

  for (const btn of buttons) {
    if (disabledScheme) {
      btn.classList.remove(disabledScheme);
    }

    btn.classList.remove('hw-carousel-navigation__control--disabled-scheme');

    for (const className of [...btn.classList]) {
      if (className.startsWith('color-') && className !== enabledScheme) {
        btn.classList.remove(className);
      }
    }

    btn.classList.add(enabledScheme);
  }
}

/**
 * Enable disabled-opacity styling by syncing prev/next disabled from scroll position.
 * @param {Element | null | undefined} block
 */
function syncUpsellsNavDisabled(block) {
  const root =
    block?.classList?.contains('hw-cart-upsells')
      ? block
      : block?.closest?.('.hw-cart-upsells');
  if (!root) return;

  const slider = root.querySelector('.hw-cart-upsells__slider');
  const nav = root.querySelector('.hw-carousel-navigation__arrows');
  if (!slider || !nav) return;

  const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
  const atStart = slider.scrollLeft <= 1;
  const atEnd = maxScroll <= 1 || slider.scrollLeft >= maxScroll - 1;

  const prev =
    nav.querySelector('[ref="previous"]') ??
    nav.querySelector('.slideshow-control--previous');
  const next =
    nav.querySelector('[ref="next"]') ??
    nav.querySelector('.slideshow-control--next');

  if (prev) prev.disabled = atStart;
  if (next) next.disabled = atEnd;

  syncUpsellsNavColorScheme(nav);
}

/**
 * @param {Element | null | undefined} scope
 */
function scheduleUpsellsNavDisabledSync(scope) {
  const block =
    scope?.classList?.contains('hw-cart-upsells')
      ? scope
      : scope?.closest?.('.hw-cart-upsells');
  if (!block) return;
  const slider = block.querySelector('.hw-cart-upsells__slider');
  if (!slider) return;

  const sync = () => syncUpsellsNavDisabled(block);
  slider.addEventListener('scrollend', sync, { once: true });
  requestAnimationFrame(sync);
  window.setTimeout(sync, 350);
}

/**
 * @param {Element | Document | null | undefined} [scope]
 */
function bindUpsellsNavDisabled(scope) {
  const blocks = scope?.closest?.('.hw-cart-upsells')
    ? [scope.closest('.hw-cart-upsells')]
    : [...document.querySelectorAll('.hw-cart-upsells:not(.hw-cart-upsells--pdp)')];

  for (const block of blocks) {
    if (!block) continue;
    const slider = block.querySelector('.hw-cart-upsells__slider');
    if (!slider || slider.dataset.cuNavScrollBound === 'true') continue;
    slider.dataset.cuNavScrollBound = 'true';

    const sync = () => syncUpsellsNavDisabled(block);
    slider.addEventListener('scroll', sync, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(sync).observe(slider);
    }
    sync();
  }
}

// ── Quick Add to Cart ─────────────────────────────────────────────────────────

function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

function setAtcButtonState(btn, state, originalText) {
  switch (state) {
    case 'loading':
      btn.disabled = true;
      btn.dataset.originalText = btn.textContent?.trim() ?? '';
      btn.textContent = '…';
      break;
    case 'added':
      btn.textContent = btn.dataset.addedText || '✓';
      break;
    case 'reset':
      btn.disabled = false;
      btn.textContent = originalText || btn.dataset.originalText || btn.textContent;
      break;
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element
    ? e.target.closest('.hw-cart-upsells__atc-btn')
    : null;
  if (!btn || btn.disabled) return;
  e.preventDefault();
  e.stopPropagation();

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const originalText = btn.textContent?.trim() ?? '';
  setAtcButtonState(btn, 'loading');

  const sectionIds = getCartSectionIds();
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: parseInt(variantId, 10),
      quantity: 1,
      ...(sectionIds && { sections: sectionIds }),
    }),
  })
    .then((r) => r.json())
    .then((response) => {
      setAtcButtonState(btn, 'added');
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { sections: response.sections ?? {} } },
      }));
      setTimeout(() => setAtcButtonState(btn, 'reset', originalText), 1500);
    })
    .catch(() => setAtcButtonState(btn, 'reset', originalText));
});

// ── S&D Recommendations ───────────────────────────────────────────────────────

/**
 * Build an image element for a product.
 * @param {object} product
 * @param {string} width  - URL width param e.g. '200'
 * @returns {HTMLImageElement|null}
 */
function buildProductImg(product, width) {
  const featImg = product.featured_image;
  const imgSrc = typeof featImg === 'string' ? featImg : (featImg?.src ?? null);
  if (!imgSrc) return null;
  const img = document.createElement('img');
  const abs = imgSrc.startsWith('//') ? `https:${imgSrc}` : imgSrc;
  img.src = abs.includes('?') ? `${abs}&width=${width}` : `${abs}?width=${width}`;
  img.alt = (typeof featImg === 'object' && featImg?.alt) || product.title;
  img.loading = 'lazy';
  img.className = 'hw-cart-upsells__rec-img';
  return img;
}

/**
 * Build a formatted price string.
 * @param {number} priceInCents
 * @param {string} [currency]
 * @returns {string}
 */
function formatPrice(priceInCents, currency) {
  return (priceInCents / 100).toLocaleString(
    document.documentElement.lang || navigator.language,
    {
      style: 'currency',
      currency: currency || window.Shopify?.currency?.active || 'GBP',
    }
  );
}

/**
 * @param {Element} container
 * @returns {boolean}
 */
function resolvePriceFromEnabled(container) {
  const mode = container.dataset.cuPriceFromMode || 'theme';
  if (mode === 'off') return false;
  if (mode === 'on') return true;
  return container.dataset.cuPriceFromTheme === 'true';
}

/**
 * @param {Array<{id:number,showFrom?:boolean,priceMin?:number,priceMax?:number}>|undefined} items
 * @returns {Map<number, {showFrom: boolean, priceMin?: number, priceMax?: number}>}
 */
function buildPriceMetaMap(items) {
  const m = new Map();
  for (const row of items || []) {
    if (row?.id == null) continue;
    m.set(Number(row.id), {
      showFrom: row.showFrom === true,
      priceMin: row.priceMin,
      priceMax: row.priceMax,
    });
  }
  return m;
}

/**
 * @param {object} product
 * @param {Map<number, {showFrom: boolean, priceMin?: number, priceMax?: number}>|undefined} priceMetaMap
 * @param {string} [priceFromMode] - theme | on | off (from block setting)
 * @returns {boolean}
 */
function productQualifiesForFrom(product, priceMetaMap, priceFromMode) {
  if (priceFromMode === 'on') {
    return true;
  }

  const row = priceMetaMap?.get(Number(product.id));
  if (row?.showFrom) {
    return true;
  }

  const variantCount = product.variants?.length ?? 0;
  if (variantCount > 1) return true;
  if (product.price_varies) return true;

  const min = product.price_min ?? product.price;
  const max = product.price_max ?? product.price;
  if (min != null && max != null && min !== max) return true;

  return false;
}

/**
 * Compare-at from first available variant (matches hw-price Liquid).
 * @param {object} product
 * @returns {number}
 */
function getVariantCompareAtCents(product) {
  const variants = product.variants;
  if (!variants?.length) return 0;
  const variant = variants.find((v) => v.available) || variants[0];
  return variant?.compare_at_price ?? 0;
}

/**
 * @param {object} product
 * @param {number} saleCents
 * @param {string} [regularLabel]
 * @returns {HTMLSpanElement|null}
 */
function buildCompareAtGroup(product, saleCents, regularLabel) {
  const compareCents = getVariantCompareAtCents(product);
  if (!compareCents || compareCents <= saleCents) return null;

  const group = document.createElement('span');
  group.setAttribute('role', 'group');

  const hidden = document.createElement('span');
  hidden.className = 'visually-hidden';
  hidden.textContent = `${regularLabel || 'Regular price'}\u00a0`;

  const compare = document.createElement('span');
  compare.className = 'compare-at-price';
  compare.textContent = formatPrice(compareCents, product.price_currency);

  group.append(hidden, compare);
  return group;
}

/**
 * @param {object} product
 * @param {object} priceOpts
 * @param {Map<number, {showFrom: boolean, priceMin?: number, priceMax?: number}>|undefined} priceMetaMap
 * @returns {HTMLDivElement}
 */
function buildRecPriceWrap(product, priceOpts, priceMetaMap) {
  const wrap = document.createElement('div');
  const classes = ['hw-cart-upsells__rec-price-wrap', 'text-block', 'text-block--no-fluid'];
  if (priceOpts.priceClass) {
    for (const part of priceOpts.priceClass.split(/\s+/)) {
      if (part) classes.push(part);
    }
  }
  wrap.className = classes.join(' ');
  if (priceOpts.priceStyle) wrap.setAttribute('style', priceOpts.priceStyle);

  const container = document.createElement('div');
  container.setAttribute('ref', 'priceContainer');

  const row = priceMetaMap?.get(Number(product.id));
  const cents =
    row?.priceMin != null
      ? row.priceMin
      : product.price_min ?? product.price;
  const formatted = formatPrice(cents, product.price_currency);

  const showFrom =
    priceOpts.priceFromEnabled &&
    productQualifiesForFrom(product, priceMetaMap, priceOpts.priceFromMode);

  if (showFrom) {
    const announce = (priceOpts.fromAnnounce || '').replace('__PRICE__', formatted);
    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = announce;

    const line = document.createElement('span');
    line.className = 'price hw-price__from-line';
    line.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    const labelClasses = ['hw-price__from-label'];
    if (priceOpts.fromLabelClass) {
      for (const part of priceOpts.fromLabelClass.split(/\s+/)) {
        if (part) labelClasses.push(part);
      }
    }
    label.className = labelClasses.join(' ');
    if (priceOpts.fromLabelStyle) label.setAttribute('style', priceOpts.fromLabelStyle);
    label.textContent = priceOpts.fromPrefix || 'From';

    const amount = document.createElement('span');
    amount.className = 'hw-price__from-amount';
    amount.textContent = formatted;

    line.append(label, amount);
    container.append(hidden, line);

    const compareGroup = buildCompareAtGroup(
      product,
      cents,
      priceOpts.priceRegularLabel
    );
    if (compareGroup) container.append(compareGroup);
  } else {
    const priceEl = document.createElement('span');
    priceEl.className = 'price';
    priceEl.textContent = formatted;
    container.append(priceEl);

    const compareGroup = buildCompareAtGroup(
      product,
      cents,
      priceOpts.priceRegularLabel
    );
    if (compareGroup) container.append(compareGroup);
  }

  wrap.append(container);
  return wrap;
}

/**
 * Read a product metafield string for card background (namespace.key).
 * Handles API shapes where the field is a string or { value: ... } (e.g. color).
 * @param {object} product
 * @param {string} key - 'namespace.key'
 * @returns {string}
 */
function readProductMetafieldString(product, key) {
  if (!product || !key) return '';
  const parts = key.split('.');
  if (parts.length !== 2) return '';
  const node = product.metafields?.[parts[0]]?.[parts[1]];
  if (node == null || node === '') return '';
  if (typeof node === 'object' && node.value != null && node.value !== '') {
    return String(node.value);
  }
  if (typeof node === 'string') return node;
  return String(node);
}

/** Section Rendering API bridge (sections/hw-cart-upsells-mf-bridge.liquid). */
const MF_BRIDGE_SECTION_ID = 'hw-cart-upsells-mf-bridge';
const MF_CART_ATTR = 'hw_cu_mf_keys';

/**
 * Parse JSON payload from bridge section HTML.
 * @param {string} html
 * @returns {{
 *   keys: string[],
 *   fields: Record<string, Array<{id:number,v:string|null}>>,
 *   mfKey: string|null,
 *   items: Array<{id:number,v:string|null}>,
 *   priceMeta: Array<{id:number,showFrom?:boolean,priceMin?:number,priceMax?:number}>
 * }}
 */
function parseMfBridgeHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const script = doc.querySelector('script[data-hw-cu-mf-map]');
  if (!script || !script.textContent?.trim()) {
    return { keys: [], fields: {}, mfKey: null, items: [], priceMeta: [] };
  }
  try {
    const parsed = JSON.parse(script.textContent.trim());
    return {
      keys: Array.isArray(parsed.keys) ? parsed.keys : [],
      fields: parsed.fields && typeof parsed.fields === 'object' ? parsed.fields : {},
      mfKey: parsed.mfKey ?? null,
      items: Array.isArray(parsed.items) ? parsed.items : [],
      priceMeta: Array.isArray(parsed.priceMeta) ? parsed.priceMeta : [],
    };
  } catch (_) {
    return { keys: [], fields: {}, mfKey: null, items: [], priceMeta: [] };
  }
}

/**
 * @param {Array<{id:number,v:string|null}>|undefined} items
 * @returns {Map<number, string>}
 */
function buildMfMapFromItems(items) {
  const m = new Map();
  for (const row of items || []) {
    if (row && row.id != null && row.v != null && row.v !== '') {
      m.set(Number(row.id), String(row.v));
    }
  }
  return m;
}

/**
 * @param {ReturnType<typeof parseMfBridgeHtml>} parsed
 * @returns {Map<string, Map<number, string>>}
 */
function buildMfFieldMaps(parsed) {
  const maps = new Map();
  if (parsed.fields && typeof parsed.fields === 'object') {
    for (const [key, items] of Object.entries(parsed.fields)) {
      if (key) {
        maps.set(key, buildMfMapFromItems(items));
      }
    }
  }
  if (maps.size === 0 && parsed.mfKey && parsed.items?.length) {
    maps.set(String(parsed.mfKey), buildMfMapFromItems(parsed.items));
  }
  return maps;
}

/**
 * @param {Map<string, Map<number, string>>|undefined} mfFieldMaps
 * @param {string} key
 * @param {number} productId
 * @returns {string}
 */
function getMfFieldValue(mfFieldMaps, key, productId) {
  if (!mfFieldMaps || !key) return '';
  const map = mfFieldMaps.get(key);
  if (!map) return '';
  return map.get(Number(productId)) || '';
}

/**
 * @param {Element} container
 * @returns {string[]}
 */
function collectMfBridgeKeys(container) {
  const keys = new Set();
  (container.dataset.cuMfBridgeKeys || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((k) => keys.add(k));
  if (container.dataset.cuBgSource === 'metafield') {
    const k = (container.dataset.cuBgMetafield || '').trim();
    if (k) keys.add(k);
  }
  if (container.dataset.cuTitleSource === 'metafield') {
    const k = (container.dataset.cuTitleMetafield || '').trim();
    if (k) keys.add(k);
  }
  if (container.dataset.cuDescSource === 'metafield') {
    const k = (container.dataset.cuDescMetafield || '').trim();
    if (k) keys.add(k);
  }
  return [...keys];
}

/**
 * @param {Element} container
 * @returns {boolean}
 */
function needsMfBridge(container) {
  return collectMfBridgeKeys(container).length > 0;
}

/**
 * Fetch mf-bridge when metafields and/or “From” price qualification (selling plans) are needed.
 * @param {Element} container
 * @returns {boolean}
 */
function needsBridgeFetch(container) {
  if (needsMfBridge(container)) return true;
  if (container.dataset.cuShowPrice === 'false') return false;
  return resolvePriceFromEnabled(container);
}

/**
 * Sync cart attribute so mf-bridge Liquid can read requested keys.
 * @param {string[]} keys
 */
async function syncCartMfKeysAttribute(keys) {
  const next = keys.join(',');
  if (!next) return;

  const cart = await fetch('/cart.js').then((r) => r.json());
  const current = String(cart.attributes?.[MF_CART_ATTR] ?? '');
  if (current === next) return;

  const attributes = { ...(cart.attributes || {}), [MF_CART_ATTR]: next };
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  });
}

/**
 * @param {string} html
 * @returns {string}
 */
function stripHtmlToText(html) {
  return String(html).replace(/<[^>]*>/g, '').trim();
}

/**
 * @param {object} product
 * @param {string} titleSource
 * @param {string} titleMfKey
 * @param {Map<string, Map<number, string>>|undefined} mfFieldMaps
 * @returns {string}
 */
function resolveCardTitle(product, titleSource, titleMfKey, mfFieldMaps) {
  if (titleSource === 'metafield' && titleMfKey) {
    const fromMf = getMfFieldValue(mfFieldMaps, titleMfKey, product.id);
    if (fromMf) return fromMf;
    const fallback = readProductMetafieldString(product, titleMfKey);
    if (fallback) return fallback;
  }
  return product.title || '';
}

/**
 * @param {object} product
 * @param {string} descSource
 * @param {string} descMfKey
 * @param {Map<string, Map<number, string>>|undefined} mfFieldMaps
 * @returns {string}
 */
function resolveCardDescription(product, descSource, descMfKey, mfFieldMaps) {
  if (descSource === 'none') return '';
  let raw = '';
  if (descSource === 'metafield' && descMfKey) {
    raw = getMfFieldValue(mfFieldMaps, descMfKey, product.id);
    if (!raw) raw = readProductMetafieldString(product, descMfKey);
  } else if (descSource === 'product_description' && product.description) {
    raw = stripHtmlToText(product.description);
  }
  if (!raw) return '';
  return raw.length > 60 ? `${raw.substring(0, 60)}…` : raw;
}

/**
 * Build a rec card element.
 * @param {object} product
 * @param {boolean} showTitle
 * @param {boolean} showPrice
 * @param {string}  quickAdd   - 'inherit' | 'show' | 'hide'
 * @param {string}  [cardStyle]  - 'style_1' | 'style_2' (default: 'style_1')
 * @param {string}  [bgSource]   - 'color_scheme' | 'metafield' (default: 'color_scheme')
 * @param {string}  [bgMetafieldKey] - 'namespace.key' (default: 'custom.card_background_color')
 * @param {boolean} [bgInherit] - Whether to inherit color scheme as fallback (default: true)
 * @param {string}  [bgColorScheme] - Color scheme to use when not inheriting (default: 'scheme-1')
 * @param {string}  [containerColorScheme] - The container's color scheme for inheritance (default: 'scheme-1')
 * @param {string}  [buttonClass] - Full button class list from Liquid (style + preset/custom)
 * @param {string}  [buttonInlineStyle] - Inline typography/style for custom button preset
 * @param {string}  [buttonColorClass] - Optional color-* scheme class
 * @param {boolean} [buttonViewOnly] - Link to PDP with “View” instead of quick add
 * @param {Map<string, Map<number, string>>} [mfFieldMaps] - Metafield values from bridge
 * @param {string} [titleSource] - product_title | metafield
 * @param {string} [titleMfKey]
 * @param {string} [descSource] - none | product_description | metafield
 * @param {string} [descMfKey]
 */
/**
 * @param {string} baseClass
 * @param {string} [typeClass]
 * @param {string} [typoStyle]
 * @param {string} [textContent]
 * @returns {HTMLParagraphElement}
 */
function buildRecTextEl(baseClass, typeClass, typoStyle, textContent) {
  const el = document.createElement('p');
  const classes = ['text-block', 'text-block--no-fluid', baseClass];
  if (typeClass) {
    for (const part of typeClass.split(/\s+/)) {
      if (part) classes.push(part);
    }
  }
  el.className = classes.join(' ');
  if (typoStyle) el.setAttribute('style', typoStyle);
  if (textContent != null) el.textContent = textContent;
  return el;
}

function buildRecCard(
  product,
  showTitle,
  showPrice,
  quickAdd,
  cardStyle,
  bgSource,
  bgMetafieldKey,
  bgInherit,
  bgColorScheme,
  containerColorScheme,
  buttonClass,
  buttonInlineStyle,
  buttonColorClass,
  buttonViewOnly,
  mfFieldMaps,
  texts,
  titleSource,
  titleMfKey,
  descSource,
  descMfKey,
  cardTypography,
  priceMetaMap,
  priceFromOpts
) {
  const card = document.createElement('div');
  card.className = 'hw-cart-upsells__rec-card';
  const style = cardStyle || 'style_1';
  const inherit = bgInherit;
  const fallbackScheme = inherit ? (containerColorScheme || 'scheme-1') : (bgColorScheme || 'scheme-1');
  const atcClass = `hw-cart-upsells__atc-btn ${buttonClass || 'button-secondary button-preset-1'}${buttonColorClass ? ` ${buttonColorClass}` : ''}`.trim();
  const viewOnly = buttonViewOnly === true || buttonViewOnly === 'true';
  const cardTitle = resolveCardTitle(
    product,
    titleSource || 'product_title',
    titleMfKey || '',
    mfFieldMaps
  );
  const cardDescription = resolveCardDescription(
    product,
    descSource || 'none',
    descMfKey || '',
    mfFieldMaps
  );
  const typo = cardTypography || {};

  // Apply background color from metafield for both card styles
  let hasMetafieldBg = false;
  let mfResolved = '';
  if (bgSource === 'metafield' && bgMetafieldKey) {
    mfResolved = getMfFieldValue(mfFieldMaps, bgMetafieldKey, product.id);
    if (!mfResolved) {
      mfResolved = readProductMetafieldString(product, bgMetafieldKey);
    }
    if (mfResolved) {
      card.style.setProperty('--hw-cu-card-bg', mfResolved);
      hasMetafieldBg = true;
    }
  }

  // Always scope scheme tokens on the card (matches collection Liquid: every card has
  // color-{{ cu_card_color_scheme }}). Metafield bg still wins via --hw-cu-card-bg in CSS.
  // Skipping the class when a metafield bg was set left --color-foreground unset in some
  // cases (e.g. display:contents parent), so text fell back to browser default (black).
  card.classList.add('color-' + fallbackScheme);

  if (style === 'style_2') {
    // ── Style 2: Horizontal — Image | Info | Button ───────────────────────
    const img = buildProductImg(product, '320');
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'hw-cart-upsells__rec-link';
    link.setAttribute('aria-label', cardTitle);
    if (img) link.append(img);
    card.append(link);

    const info = document.createElement('div');
    info.className = 'hw-cart-upsells__rec-info';

    if (showTitle) {
      info.append(
        buildRecTextEl(
          'hw-cart-upsells__rec-title',
          typo.titleClass,
          typo.titleStyle,
          cardTitle
        )
      );
    }

    if (cardDescription) {
      info.append(
        buildRecTextEl(
          'hw-cart-upsells__rec-desc',
          typo.descClass,
          typo.descStyle,
          cardDescription
        )
      );
    }

    if (showPrice) {
      info.append(
        buildRecPriceWrap(
          product,
          {
            priceClass: typo.priceClass,
            priceStyle: typo.priceStyle,
            ...priceFromOpts,
          },
          priceMetaMap
        )
      );
    }
    card.append(info);

    if (quickAdd !== 'hide') {
      const actions = document.createElement('div');
      actions.className = 'hw-cart-upsells__rec-actions';
      appendRecCardCta(actions, product, atcClass, buttonInlineStyle, viewOnly, texts);
      card.append(actions);
    }
  } else {
    // ── Style 1: Vertical stacked (original) ─────────────────────────────
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'hw-cart-upsells__rec-link';
    link.setAttribute('aria-label', cardTitle);

    const img = buildProductImg(product, '400');
    if (img) link.append(img);

    const info = document.createElement('div');
    info.className = 'hw-cart-upsells__rec-info';
    if (showTitle) {
      info.append(
        buildRecTextEl(
          'hw-cart-upsells__rec-title',
          typo.titleClass,
          typo.titleStyle,
          cardTitle
        )
      );
    }
    if (showPrice) {
      info.append(
        buildRecPriceWrap(
          product,
          {
            priceClass: typo.priceClass,
            priceStyle: typo.priceStyle,
            ...priceFromOpts,
          },
          priceMetaMap
        )
      );
    }
    link.append(info);
    card.append(link);

    if (quickAdd !== 'hide') {
      appendRecCardCta(card, product, atcClass, buttonInlineStyle, viewOnly, texts);
    }
  }

  return card;
}

/**
 * Quick add button or View link on a recommendation card.
 * @param {Element} parent
 * @param {object} product
 * @param {string} atcClass
 * @param {string} buttonInlineStyle
 * @param {boolean} viewOnly
 * @param {{add?: string, view?: string, soldOut?: string}} texts
 */
function appendRecCardCta(parent, product, atcClass, buttonInlineStyle, viewOnly, texts) {
  const variant =
    !viewOnly && product.variants?.length === 1 ? product.variants[0] : null;

  if (variant) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = atcClass;
    if (buttonInlineStyle) btn.setAttribute('style', buttonInlineStyle);
    btn.dataset.variantId = String(variant.id);
    btn.dataset.addedText = '✓';
    if (!variant.available) btn.disabled = true;
    btn.textContent = variant.available
      ? (texts?.add || 'Add')
      : (texts?.soldOut || 'Sold out');
    parent.append(btn);
    return;
  }

  const a = document.createElement('a');
  a.href = product.url;
  a.className = atcClass.replace('hw-cart-upsells__atc-btn', 'hw-cart-upsells__options-link');
  if (buttonInlineStyle) a.setAttribute('style', buttonInlineStyle);
  a.textContent = texts?.view || 'View';
  parent.append(a);
}

/**
 * Per-seed product cache (keyed by seed product ID).
 *
 * Used exclusively by the MutationObserver to restore cards synchronously
 * when morphSection wipes the recs target during a section re-render.
 * The cache only ever holds products for the current seed — showing cached
 * cards on a quantity-adjust means showing the exact same products the user
 * was already looking at, with zero stale-data risk.
 *
 * When the seed changes (new product added to cart), the cache has no entry
 * for the new seed, so nothing is restored and the fresh fetch runs normally.
 *
 * @type {Map<string, Array<object>>}
 */
const recsCache = new Map();

/**
 * Metafield map from Liquid bridge per seed product (recommendations + metafield bg).
 * @type {Map<string, Map<number, string>>}
 */
const mfBridgeCache = new Map();

/**
 * Repopulate a recs target from cache without triggering a network request.
 * @param {Element} target
 * @param {string}  seed
 * @param {boolean} showTitle
 * @param {boolean} showPrice
 * @param {string}  quickAdd
 * @param {string}  [cardStyle]
 * @param {string}  [bgSource]
 * @param {string}  [bgMetafieldKey]
 * @param {boolean} [bgInherit]
 * @param {string}  [bgColorScheme]
 * @param {string}  [containerColorScheme]
 * @param {string}  [buttonClass]
 * @param {string}  [buttonInlineStyle]
 * @param {string}  [buttonColorClass]
 * @param {boolean} [buttonViewOnly]
 */
/**
 * @param {Element} container
 * @returns {object}
 */
function getRecsOptionsFromContainer(container) {
  return {
    showTitle: container.dataset.cuShowTitle !== 'false',
    showPrice: container.dataset.cuShowPrice !== 'false',
    quickAdd: container.dataset.cuQuickAdd || 'inherit',
    cardStyle: container.dataset.cuCardStyle || 'style_1',
    bgSource: container.dataset.cuBgSource || 'color_scheme',
    bgMetafieldKey: container.dataset.cuBgMetafield || 'custom.card_background_color',
    bgInherit: container.dataset.cuBgInherit !== 'false',
    bgColorScheme: container.dataset.cuBgColorScheme || 'scheme-1',
    containerColorScheme:
      container.className.match(/color-([\w-]+)/)?.[1] || 'scheme-1',
    buttonClass: container.dataset.cuButtonClass || 'button-secondary button-preset-1',
    buttonInlineStyle: container.dataset.cuButtonStyle || '',
    buttonColorClass: container.dataset.cuButtonColorClass || '',
    buttonViewOnly: container.dataset.cuButtonViewOnly === 'true',
    titleSource: container.dataset.cuTitleSource || 'product_title',
    titleMfKey: container.dataset.cuTitleMetafield || '',
    descSource: container.dataset.cuDescSource || 'none',
    descMfKey: container.dataset.cuDescMetafield || '',
    cardTypography: {
      titleClass: container.dataset.cuTitleClass || '',
      titleStyle: container.dataset.cuTitleStyle || '',
      descClass: container.dataset.cuDescClass || '',
      descStyle: container.dataset.cuDescStyle || '',
      priceClass: container.dataset.cuPriceClass || '',
      priceStyle: container.dataset.cuPriceStyle || '',
    },
    priceFromEnabled: resolvePriceFromEnabled(container),
    priceFromMode: container.dataset.cuPriceFromMode || 'theme',
    fromPrefix: container.dataset.cuFromPrefix || '',
    fromAnnounce: container.dataset.cuFromAnnounce || '',
    fromLabelClass: container.dataset.cuFromLabelClass || '',
    fromLabelStyle: container.dataset.cuFromLabelStyle || '',
    priceRegularLabel: container.dataset.cuPriceRegular || '',
    texts: {
      add: container.dataset.cuTextAdd,
      view: container.dataset.cuTextView,
      soldOut: container.dataset.cuTextSoldOut,
    },
  };
}

function restoreFromCache(target, seed, container) {
  if (!recsCache.has(seed)) return;
  const opts = getRecsOptionsFromContainer(container);
  const bridgeData = mfBridgeCache.get(seed) ?? {};
  const mfFieldMaps = bridgeData.fieldMaps ?? new Map();
  const priceMetaMap = bridgeData.priceMeta ?? new Map();
  const priceFromOpts = {
    priceFromEnabled: opts.priceFromEnabled,
    priceFromMode: opts.priceFromMode,
    fromPrefix: opts.fromPrefix,
    fromAnnounce: opts.fromAnnounce,
    fromLabelClass: opts.fromLabelClass,
    fromLabelStyle: opts.fromLabelStyle,
    priceRegularLabel: opts.priceRegularLabel,
  };
  const frag = document.createDocumentFragment();
  for (const p of recsCache.get(seed)) {
    frag.append(
      buildRecCard(
        p,
        opts.showTitle,
        opts.showPrice,
        opts.quickAdd,
        opts.cardStyle,
        opts.bgSource,
        opts.bgMetafieldKey,
        opts.bgInherit,
        opts.bgColorScheme,
        opts.containerColorScheme,
        opts.buttonClass,
        opts.buttonInlineStyle,
        opts.buttonColorClass,
        opts.buttonViewOnly,
        mfFieldMaps,
        opts.texts,
        opts.titleSource,
        opts.titleMfKey,
        opts.descSource,
        opts.descMfKey,
        opts.cardTypography,
        priceMetaMap,
        priceFromOpts
      )
    );
  }
  target.replaceChildren(frag);
  bindUpsellsNavDisabled(container);
}

/**
 * Observe cart-items-component for section morphs.
 *
 * morphSection (Horizon's section renderer) runs synchronously and wipes
 * recs targets back to empty. MutationObserver callbacks fire in the same
 * JS task — before the browser has a chance to paint — so we can restore
 * the previous cards from cache with zero visible flash.
 *
 * The background fetch in loadRecommendations then quietly updates the
 * cards with fresh, cart-filtered data once the network response arrives.
 */
function attachRecsObserver() {
  const cartComp = document.querySelector('cart-items-component[data-drawer]');
  if (!cartComp) return;

  new MutationObserver(() => {
    cartComp.querySelectorAll('[data-cu-recs]').forEach((target) => {
      // Skip if the morph kept existing cards intact
      if (target.childElementCount > 0) return;

      const seed = target.dataset.cuSeed;
      if (!seed || !recsCache.has(seed)) return;

      const container = target.closest('[data-cu-source="recommendations"]');
      if (!container) return;

      restoreFromCache(target, seed, container);
    });
  }).observe(cartComp, { childList: true, subtree: true });
}

async function loadRecommendations(container) {
  const target = container.querySelector('[data-cu-recs]');
  if (!target) return;

  const seed = target.dataset.cuSeed;
  if (!seed) return;

  const limit = parseInt(container.dataset.cuLimit || '4', 10);
  const recLimit = Math.min(Math.max(limit * 2, 1), 10);
  const opts = getRecsOptionsFromContainer(container);
  const bridgeKeys = collectMfBridgeKeys(container);

  const recsUrl =
    `/recommendations/products.json?product_id=${encodeURIComponent(seed)}` +
    `&limit=${recLimit}&intent=related`;
  const bridgeUrl =
    `/recommendations/products?product_id=${encodeURIComponent(seed)}` +
    `&limit=${recLimit}&intent=related&section_id=${MF_BRIDGE_SECTION_ID}`;

  try {
    const needBridge = needsBridgeFetch(container);
    const recsPromise = fetch(recsUrl);
    const cartPromise = fetch('/cart.js');
    let bridgeResp = null;
    if (needBridge) {
      if (bridgeKeys.length) {
        await syncCartMfKeysAttribute(bridgeKeys);
      }
      bridgeResp = await fetch(bridgeUrl);
    }

    const [recsResp, cart] = await Promise.all([recsPromise, cartPromise]);
    if (!recsResp.ok) return;

    let mfFieldMaps = new Map();
    let priceMetaMap = new Map();
    if (needBridge && bridgeResp?.ok) {
      const parsed = parseMfBridgeHtml(await bridgeResp.text());
      mfFieldMaps = buildMfFieldMaps(parsed);
      priceMetaMap = buildPriceMetaMap(parsed.priceMeta);
      const bridgeKeysSet = new Set(parsed.keys || []);
      for (const key of bridgeKeys) {
        if (!mfFieldMaps.has(key)) {
          console.warn(
            '[hw-cart-upsells] Metafield bridge did not return key:',
            key,
            '— check product metafields and cart attribute',
            MF_CART_ATTR
          );
        } else if (bridgeKeysSet.size && !bridgeKeysSet.has(key)) {
          console.warn('[hw-cart-upsells] Bridge keys missing:', key);
        }
      }
    } else if (needBridge && bridgeResp && !bridgeResp.ok) {
      console.warn(
        '[hw-cart-upsells] Metafield bridge request failed:',
        bridgeResp.status,
        MF_BRIDGE_SECTION_ID
      );
    }

    if (needBridge) {
      mfBridgeCache.set(seed, { fieldMaps: mfFieldMaps, priceMeta: priceMetaMap });
    } else {
      mfBridgeCache.delete(seed);
    }

    const { products } = await recsResp.json();
    const cartProductIds = new Set(cart.items.map((item) => item.product_id));

    const filtered = products
      .filter((p) => !cartProductIds.has(p.id))
      .slice(0, limit);

    if (!filtered.length) { container.style.display = 'none'; return; }

    // Update cache before replacing DOM so the observer has the latest data
    recsCache.set(seed, filtered);

    const priceFromOpts = {
      priceFromEnabled: opts.priceFromEnabled,
      priceFromMode: opts.priceFromMode,
      fromPrefix: opts.fromPrefix,
      fromAnnounce: opts.fromAnnounce,
      fromLabelClass: opts.fromLabelClass,
      fromLabelStyle: opts.fromLabelStyle,
      priceRegularLabel: opts.priceRegularLabel,
    };
    const frag = document.createDocumentFragment();
    for (const p of filtered) {
      frag.append(
        buildRecCard(
          p,
          opts.showTitle,
          opts.showPrice,
          opts.quickAdd,
          opts.cardStyle,
          opts.bgSource,
          opts.bgMetafieldKey,
          opts.bgInherit,
          opts.bgColorScheme,
          opts.containerColorScheme,
          opts.buttonClass,
          opts.buttonInlineStyle,
          opts.buttonColorClass,
          opts.buttonViewOnly,
          mfFieldMaps,
          opts.texts,
          opts.titleSource,
          opts.titleMfKey,
          opts.descSource,
          opts.descMfKey,
          opts.cardTypography,
          priceMetaMap,
          priceFromOpts
        )
      );
    }
    target.replaceChildren(frag);
    bindUpsellsNavDisabled(container);
  } catch (_) {
    container.style.display = 'none';
  }
}

function initRecsContainers() {
  document.querySelectorAll('.hw-cart-upsells[data-cu-source="recommendations"]')
    .forEach((c) => loadRecommendations(c));
}

function initCartUpsells() {
  attachRecsObserver();
  initRecsContainers();
  bindUpsellsNavDisabled();
}

// Background refresh after cart updates — observer handles the zero-flash restore;
// this fetch ensures stale cards (e.g. a rec now in cart) are quietly removed.
document.addEventListener('cart:update', () => setTimeout(initCartUpsells, 0));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartUpsells);
} else {
  initCartUpsells();
}

document.addEventListener('shopify:block:select', initRecsContainers);
document.addEventListener('shopify:section:load', initCartUpsells);
